import { defineStore } from "pinia"
import { computed, reactive, ref } from "vue"
import { i18N, newDialog, toast } from "@/common/util"
import comunicacaoService from '@/services/comunicacao.service'

import type { CommunicationsType, LoaderType } from "@/types/types"

export const useComunicacoesStore = defineStore('comunicacoes', () => {
  const list = ref<CommunicationsType[]>([])
  const userCommunications = ref<CommunicationsType[]>([])
  const showCommunications = ref(false)
  
  const status = reactive<Record<string, LoaderType>>({
    getAll: 'initial',
    create: 'initial',
    update: 'initial',
    delete: 'initial',
    getById: 'initial',
  })

  const loading = computed(() => Object.values(status).some(el => el === 'loading'))

  const create = async (communication: CommunicationsType) => {
    status.create = 'loading'
    try {
      const { data, success } = await comunicacaoService.post(null, getFormDataSubmit(communication))
      if (success) {
        toast.success({message: i18N('toasts.createSuccess')})
        getAll()
      }
      status.create = 'success'
      return data
    } catch {
      status.create = 'error'
    }
  }

  const update = async (communication: CommunicationsType, id: string) => {
    status.update = 'loading'
    try {
      const { data, success } = await comunicacaoService.put(id, getFormDataSubmit(communication))
      if(success) {
        toast.success({message: i18N('toasts.updateSuccess')})
        const value = list.value.find(el => el.id === data.id)
        value && Object.assign(value, getFormatResponse(data))
      }
      status.update = 'success'
      return data
    } catch {
      status.update = 'error'
    }
  }

  const getAll = async (): Promise<void> => {
    status.getAll = 'loading'

    try {
      const { data } = await comunicacaoService.get<CommunicationsType[]>()
      list.value = data.map(el => getFormatResponse(el))
      status.getAll = 'success'
    } catch {
      status.getAll = 'error'
    }
  }

  const remove = async (data: CommunicationsType, force?: boolean) => {
    if(!force) 
      return newDialog.open({
        title: i18N('feedback.attention'),
        message: i18N('dialogs.delete.message', {type: i18N('labels.communication').toLowerCase(), name: data.name}),
        complete: () => remove(data, true)
      })

    try {
      status.delete = 'loading'
      const { success } = await comunicacaoService.delete(data.id)
      if (success) {
        getAll()
        toast.success({ message: i18N('toasts.removeSuccess') })
      }
      status.delete = 'success'
      return true
    } catch {
      status.delete = 'success'
    }
  }

  const getById = async (id): Promise<CommunicationsType> => {
    status.getById = 'loading'

    try {
      const { data } = await comunicacaoService.get<CommunicationsType>(id)
      status.getById = 'success'
      return getFormatResponse(data)
    } catch {
      status.getById = 'error'
    }
  }

  const getFormDataSubmit = (communication: CommunicationsType) => {
    return {
      id: communication.id,
      titulo: communication.name,
      inicio: communication.initDate,
      termino: communication.endDate,
      exibirSempre: communication.forceDisplay,
      postagens: communication.posts.map((el, i) => (el.id ?? delete el.id) && ({
        id: el.id,
        titulo: el.title,
        html: el.content,
        imagem: {
          data: el?.file?.split(',').at(-1),
          mime: el?.fileType
        },
        linkImagem: el.linkImage,
        ordem: i,
      })),
      cargos: communication.jobTitles.map((el) => ({cargoId: el})),
      departamentos: communication.departments.map((el) => ({departamentoId: el})),
      unidadeDeNegocio: communication.units.map((el) => ({unidadeDeNegocioId: el})),
      active: communication.active,
      publico: communication.publico,
      contratosComunicacoes: communication.contratosComunicacoes
    }
  }

  const getFormatResponse = (response: any): CommunicationsType => {
    return {
      id: response.id,
      name: response.titulo,
      initDate: response.inicio?.split('+')[0],
      endDate: response.termino?.split('+')[0],
      posts: response.postagens.map((el) => ({
        id: el.id,
        title: el.titulo,
        content: el.html,
        linkImage: el.linkImagem,
        order: el.ordem,
        file: el.imagem?.data,
        fileType: el.imagem?.mime
      })),
      active: response.active,
      publico: response.publico,
      origem: response.origem,
      jobTitles: response.cargos.map((el) => el.cargoId),
      departments: response.departamentos.map((el) => el.departamentoId),
      units: response.unidadeDeNegocio.map((el) => el.unidadeDeNegocioId),
      forceDisplay: response.exibirSempre,
      dontDisplay: response.usuariosComunicacoes?.length > 0,
      contratosComunicacoes: response.contratosComunicacoes,
    }
  }

  const getUserCommunication = async (showAllCommunication: boolean = false) => {
    status.getAll = 'loading'
    try {
      const { data } = await comunicacaoService.getUserCommunication({
        exibirComunicacoesDesabilitadas: showAllCommunication
      })

      status.getAll = 'success'

      if (!showAllCommunication) showCommunications.value = data.length > 0
      userCommunications.value = data.map((el) => getFormatResponse(el))

      return userCommunications.value
    } catch (error) {
      status.getAll = 'error'
    }
  }

  const toggledontDisplay = async (communications: CommunicationsType[]) => {
    if (!communications.length) return

    let displayList = []
    let dontDiplayList = []

    communications.forEach((el) => {
      const uc = userCommunications.value.find((uc) => uc.id === el.id)

      // Ignora se o valor atual for igual ao valor registrado anteriormente
      if (uc?.dontDisplay === el.dontDisplay) return

      if (el.dontDisplay) dontDiplayList.push(el.id)
      else displayList.push(el.id)
    })

    if (displayList.length) await changeUserCommunicationsStatus(displayList, true)
    if (dontDiplayList.length) await changeUserCommunicationsStatus(dontDiplayList, false)

    showCommunications.value = false
  }

  const changeUserCommunicationsStatus = async (ids: string[], display: boolean) => {
    try {
      status.update = 'loading'
      const { success } = await comunicacaoService.changeUserCommunicationsStatus(ids, display)
      status.update = 'success'
      
      return success
    } catch (error) {
      status.update = 'error'
    }
  }

  const associationContracts = async (comunicacaoId: string, contratosComunicacoesIds: string[]) => {
    status.update = 'loading'
    try {
      const { success } = await comunicacaoService.associationContracts(comunicacaoId, contratosComunicacoesIds)
      if (success) toast.open({ type: 'success', message: i18N('toasts.sections.contractLinked') })
      status.update = 'success'
    
      return success
    } catch {
      status.update = 'error'
    }
  }

  return {
    list,
    status,
    userCommunications,
    showCommunications,
    loading,
    create,
    update,
    getAll,
    remove,
    getById,
    getUserCommunication,
    toggledontDisplay,
    associationContracts
  }
})

export default useComunicacoesStore